import React, {useEffect, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import Workspace from "../components/Workspace";
import ReviewNotice from "../components/ReviewNotice";
import StatusBadge from "../components/StatusBadge";
import PhotoManager from "../components/PhotoManager";
import DocumentUpload from "../components/DocumentUpload";
import {ChipPicker, Field, Section, formatPrice, numberOrNull} from "../components/Fields";
import {COMMON_AMENITIES, FACINGS, FURNISHING, POSSESSION, PROPERTY_TYPES} from "../constants";
import {createListing, getMyListing, submitListing, updateListing} from "../api/listings";
import {parseCoordinates, validateListing} from "../validation";

const EMPTY = {
    title: "", type: "apartment", resale: "false", location: "", coordinates: "", projectName: "", developerName: "",
    price: "", bedrooms: "", bathrooms: "", sqft: "", carpetArea: "", builtupArea: "", maintenance: "", brokerage: "",
    furnishing: "", facing: "", totalFloors: "", liftCount: "", blockCount: "", ageOfConstructionYears: "",
    possessionStatus: "", possessionDate: "", parking: "", vastuCompliant: "", reraNumber: "", kathaStatus: "",
    description: "", amenities: [], mainImages: [], floorPlanUrl: "", brochureFile: "", videoUrl: "",
};

const toForm = (listing) => {
    const merged = {...listing, ...(listing.pendingChanges || {})};
    const out = {...EMPTY};
    Object.keys(EMPTY).forEach((k) => {
        if (merged[k] !== null && merged[k] !== undefined) out[k] = Array.isArray(merged[k]) ? merged[k] : String(merged[k]);
    });
    out.price = merged.priceRaw ?? merged.price ?? "";
    if (listing.pendingChanges?.price) out.price = listing.pendingChanges.price;
    out.mainImages = merged.mainImages?.length ? merged.mainImages : (merged.images || []);
    out.resale = merged.resale ? "true" : "false";
    out.vastuCompliant = merged.vastuCompliant ? "true" : "";
    out.coordinates = merged.latitude != null && merged.longitude != null ? `${merged.latitude}, ${merged.longitude}` : "";
    return out;
};

const toPayload = (f) => {
    const text = (v) => (v && String(v).trim()) || null;
    const coords = parseCoordinates(f.coordinates);
    return {
        title: text(f.title), type: f.type, resale: f.resale === "true", location: text(f.location),
        latitude: coords?.[0] ?? null, longitude: coords?.[1] ?? null,
        projectName: text(f.projectName), developerName: text(f.developerName),
        price: numberOrNull(f.price), bedrooms: numberOrNull(f.bedrooms), bathrooms: numberOrNull(f.bathrooms),
        sqft: numberOrNull(f.sqft), carpetArea: text(f.carpetArea), builtupArea: text(f.builtupArea),
        maintenance: text(f.maintenance), brokerage: text(f.brokerage), furnishing: text(f.furnishing),
        facing: text(f.facing), totalFloors: numberOrNull(f.totalFloors), liftCount: numberOrNull(f.liftCount),
        blockCount: numberOrNull(f.blockCount), ageOfConstructionYears: numberOrNull(f.ageOfConstructionYears),
        possessionStatus: text(f.possessionStatus), possessionDate: text(f.possessionDate), parking: text(f.parking),
        vastuCompliant: f.vastuCompliant === "true" ? true : null, reraNumber: text(f.reraNumber),
        reraApproved: text(f.reraNumber) ? true : null, kathaStatus: text(f.kathaStatus), description: text(f.description),
        amenities: f.amenities, mainImages: f.mainImages, floorPlanUrl: text(f.floorPlanUrl),
        brochureFile: text(f.brochureFile), videoUrl: text(f.videoUrl),
    };
};

const ListingEditor = ({broker}) => {
    const {id} = useParams();
    const navigate = useNavigate();
    const isNew = !id;
    const [listing, setListing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState({loading: !isNew, saving: false, error: "", saved: ""});

    useEffect(() => {
        if (isNew) return;
        getMyListing(id)
            .then((l) => {
                if (l.projectId) navigate(`/broker/projects/${l.projectId}`, {replace: true});
                setListing(l);
                setForm(toForm(l));
                setStatus((s) => ({...s, loading: false}));
            })
            .catch((err) => setStatus({loading: false, saving: false, error: err.message, saved: ""}));
    }, [id, isNew, navigate]);

    const set = (k) => (e) => {
        const value = e?.target ? e.target.value : e;
        setForm((f) => ({...f, [k]: value}));
        setErrors((er) => ({...er, [k]: undefined}));
    };

    const reviewStatus = listing?.reviewStatus || "DRAFT";
    const canSubmit = ["DRAFT", "CHANGES_REQUESTED"].includes(reviewStatus);
    const live = reviewStatus === "APPROVED";
    const approvedBroker = broker.status === "APPROVED";

    const save = async (submit) => {
        const found = validateListing(form, {submit});
        setErrors(found);
        if (Object.keys(found).length) {
            const fields = Object.keys(found).length;
            setStatus((s) => ({...s, error: fields === 1 ? Object.values(found)[0] : `Please fix the ${fields} highlighted fields.`, saved: ""}));
            setTimeout(() => document.querySelector(".brk-field--error")?.scrollIntoView({behavior: "smooth", block: "center"}), 50);
            return;
        }
        setStatus({loading: false, saving: true, error: "", saved: ""});
        try {
            let saved = isNew ? await createListing(toPayload(form)) : await updateListing(id, toPayload(form));
            if (submit) {
                await submitListing(saved.id);
                saved = await getMyListing(saved.id);
            }
            setListing(saved);
            setForm(toForm(saved));
            const message = submit ? "Submitted for review. We'll email you when it's decided."
                : live && saved.pendingChanges ? "Saved. Your changes go live once our team reviews them."
                    : "Saved.";
            setStatus({loading: false, saving: false, error: "", saved: message});
            if (isNew) navigate(`/broker/listings/${saved.id}`, {replace: true});
            window.scrollTo({top: 0, behavior: "smooth"});
        } catch (err) {
            setStatus({loading: false, saving: false, error: err.message, saved: ""});
        }
    };

    if (status.loading) return <div className="brk-loading">Loading listing…</div>;
    if (!approvedBroker) {
        return <div className="brk-card"><p>You can add listings once your account is approved.</p>
            <Link className="brk-link" to="/broker/dashboard">Back to dashboard</Link></div>;
    }
    if (listing?.reviewStatus === "REJECTED") {
        return <div className="brk-card"><ReviewNotice status="REJECTED" note={listing.reviewNote}/>
            <Link className="brk-link" to="/broker/dashboard?tab=listings">Back to listings</Link></div>;
    }

    return (
        <form className="brk-editor" onSubmit={(e) => { e.preventDefault(); save(false); }} noValidate>
            <div className="brk-page-head">
                <div>
                    <Link to="/broker/dashboard?tab=listings" className="brk-back">← Listings</Link>
                    <h1 className="brk-page-title">{isNew ? "Add a listing" : form.title || "Edit listing"}</h1>
                </div>
                {listing && <StatusBadge status={listing.reviewStatus} active={listing.active} pendingChanges={listing.pendingChanges}/>}
            </div>
            {listing && <ReviewNotice status={listing.reviewStatus} note={listing.reviewNote} pendingChanges={listing.pendingChanges}/>}
            {status.saved && <div className="brk-notice brk-notice--ok" role="status">{status.saved}</div>}

            <Section title="Basics">
                <Field label="Title" required wide error={errors.title} hint="What buyers see first, e.g. “Spacious 3 BHK with lake view”">
                    <input className="brk-input" value={form.title} onChange={set("title")} maxLength={200}/>
                </Field>
                <Field label="Property type">
                    <select className="brk-input" value={form.type} onChange={set("type")}>
                        {PROPERTY_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                    </select>
                </Field>
                <Field label="Sale type">
                    <select className="brk-input" value={form.resale} onChange={set("resale")}>
                        <option value="false">New property</option>
                        <option value="true">Resale</option>
                    </select>
                </Field>
                <Field label="Area / locality" required error={errors.location} hint="e.g. HSR Layout, Whitefield">
                    <input className="brk-input" value={form.location} onChange={set("location")} maxLength={150}/>
                </Field>
                <Field label="Map position" error={errors.coordinates}
                       hint="Optional: paste a Google Maps link or “12.9121, 77.6446”. Left empty, we place it from the area.">
                    <input className="brk-input" value={form.coordinates} onChange={set("coordinates")} placeholder="Latitude, longitude"/>
                </Field>
                <Field label="Project / building name"><input className="brk-input" value={form.projectName} onChange={set("projectName")}/></Field>
                <Field label="Developer"><input className="brk-input" value={form.developerName} onChange={set("developerName")}/></Field>
            </Section>

            <Section title="Price & size">
                <Field label="Price (₹)" required error={errors.price} hint={formatPrice(form.price) || "Total price in rupees"}>
                    <input className="brk-input" type="number" min="0" inputMode="numeric" value={form.price} onChange={set("price")}/>
                </Field>
                <Field label="Bedrooms" error={errors.bedrooms}><input className="brk-input" type="number" min="0" value={form.bedrooms} onChange={set("bedrooms")}/></Field>
                <Field label="Bathrooms" error={errors.bathrooms}><input className="brk-input" type="number" min="0" value={form.bathrooms} onChange={set("bathrooms")}/></Field>
                <Field label="Super built-up area (sqft)" error={errors.sqft}><input className="brk-input" type="number" min="0" value={form.sqft} onChange={set("sqft")}/></Field>
                <Field label="Carpet area"><input className="brk-input" value={form.carpetArea} onChange={set("carpetArea")} placeholder="e.g. 1,250 sqft"/></Field>
                <Field label="Maintenance"><input className="brk-input" value={form.maintenance} onChange={set("maintenance")} placeholder="e.g. ₹4/sqft per month"/></Field>
                <Field label="Brokerage"><input className="brk-input" value={form.brokerage} onChange={set("brokerage")} placeholder="e.g. No brokerage"/></Field>
            </Section>

            <Section title="Details">
                <Field label="Furnishing">
                    <select className="brk-input" value={form.furnishing} onChange={set("furnishing")}>
                        <option value="">—</option>{FURNISHING.map((v) => <option key={v}>{v}</option>)}
                    </select>
                </Field>
                <Field label="Facing">
                    <select className="brk-input" value={form.facing} onChange={set("facing")}>
                        <option value="">—</option>{FACINGS.map((v) => <option key={v}>{v}</option>)}
                    </select>
                </Field>
                <Field label="Possession">
                    <select className="brk-input" value={form.possessionStatus} onChange={set("possessionStatus")}>
                        <option value="">—</option>{POSSESSION.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                </Field>
                <Field label="Possession date"><input className="brk-input" type="date" value={form.possessionDate} onChange={set("possessionDate")}/></Field>
                <Field label="Floors in building"><input className="brk-input" type="number" min="0" value={form.totalFloors} onChange={set("totalFloors")}/></Field>
                <Field label="Lifts"><input className="brk-input" type="number" min="0" value={form.liftCount} onChange={set("liftCount")}/></Field>
                <Field label="Blocks / towers"><input className="brk-input" type="number" min="0" value={form.blockCount} onChange={set("blockCount")}/></Field>
                <Field label="Age (years)"><input className="brk-input" type="number" min="0" value={form.ageOfConstructionYears} onChange={set("ageOfConstructionYears")}/></Field>
                <Field label="Parking"><input className="brk-input" value={form.parking} onChange={set("parking")} placeholder="e.g. 1 covered"/></Field>
                <Field label="Vastu">
                    <select className="brk-input" value={form.vastuCompliant} onChange={set("vastuCompliant")}>
                        <option value="">Not stated</option><option value="true">Vastu compliant</option>
                    </select>
                </Field>
                <Field label="Project RERA number" error={errors.reraNumber}><input className="brk-input" value={form.reraNumber} onChange={set("reraNumber")}/></Field>
                <Field label="Khata"><input className="brk-input" value={form.kathaStatus} onChange={set("kathaStatus")} placeholder="e.g. A Khata"/></Field>
            </Section>

            <Section title="Amenities">
                <Field label="Select all that apply" wide group>
                    <ChipPicker options={COMMON_AMENITIES} value={form.amenities} onChange={set("amenities")}/>
                </Field>
                <Field label="Description" wide error={errors.description} hint={`${form.description.length}/3000`}>
                    <textarea className="brk-input" rows={5} maxLength={3000} value={form.description} onChange={set("description")}
                              placeholder="What makes this home worth a visit: layout, view, neighbourhood, nearby schools and offices…"/>
                </Field>
            </Section>

            <Section title="Photos & documents" subtitle="Listings with good photos get far more enquiries.">
                <Field label="Photos" wide group required error={errors.mainImages}>
                    <PhotoManager value={form.mainImages} onChange={set("mainImages")}/>
                </Field>
                <Field label="Floor plan" group><DocumentUpload value={form.floorPlanUrl} onChange={set("floorPlanUrl")} label="Upload floor plan"/></Field>
                <Field label="Brochure (PDF)" group>
                    <DocumentUpload value={form.brochureFile} onChange={set("brochureFile")} label="Upload brochure" accept="application/pdf"/>
                </Field>
                <Field label="Video link" error={errors.videoUrl} hint="YouTube or another public link">
                    <input className="brk-input" value={form.videoUrl} onChange={set("videoUrl")} placeholder="https://"/>
                </Field>
            </Section>

            <div className="brk-form-foot brk-form-foot--sticky">
                {status.error && <p className="brk-form-error" role="alert">⚠ {status.error}</p>}
                <Link to="/broker/dashboard?tab=listings" className="brk-btn brk-btn--ghost">Cancel</Link>
                <button type="submit" className="brk-btn brk-btn--ghost" disabled={status.saving}>
                    {status.saving ? "Saving…" : live ? "Save changes" : "Save draft"}
                </button>
                {(isNew || canSubmit) && (
                    <button type="button" className="brk-btn brk-btn--primary" disabled={status.saving} onClick={() => save(true)}>
                        Submit for review
                    </button>
                )}
            </div>
        </form>
    );
};

const ListingEditorPage = () => <Workspace>{({broker}) => <ListingEditor broker={broker}/>}</Workspace>;

export default ListingEditorPage;
